/**
 * External store for removed front face features (homeButton, frontSpeaker) — red ghost display.
 * Keyed by feature ID.
 * Entries persist until the worker finishes recomputing geometry.
 */

/** Shape data for a removed home button */
export interface RemovedHomeButtonGhost {
  type: 'homeButton'
  radius: number
  x: number
  y: number
}

/** Shape data for a removed front speaker */
export interface RemovedFrontSpeakerGhost {
  type: 'frontSpeaker'
  width: number
  height: number
  cornerRadius: number
  xOffset: number
  yOffset: number
}

export type RemovedFrontFeatureGhost = RemovedHomeButtonGhost | RemovedFrontSpeakerGhost

let snapshot: Record<string, RemovedFrontFeatureGhost> | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((l) => l())

/**
 * Record a removed front face feature for red ghost display.
 * @param id — feature ID
 * @param ghost — full shape data at the time of removal
 */
export const setRemovedFrontFeature = (id: string, ghost: RemovedFrontFeatureGhost) => {
  snapshot = {...snapshot, [id]: ghost}
  notify()
}

export const clearRemovedFrontFeature = () => {
  snapshot = null
  notify()
}

export const getRemovedFrontFeatureSnapshot = (): Record<string, RemovedFrontFeatureGhost> | null => snapshot

export const subscribeRemovedFrontFeature = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
